import { useEffect, useRef } from 'react';
import OktaSignIn from '@okta/okta-signin-widget';
import { withOktaAuth } from '@okta/okta-react';
import type { OktaAuth, AuthState } from '@okta/okta-auth-js';

import { useOkta } from 'utils/okta/use-okta';

import '@okta/okta-signin-widget/css/okta-sign-in.min.css';
import 'containers/login/okta-widget/okta-theme.css';

interface OktaInjectedProps {
  oktaAuth: OktaAuth;
  authState: AuthState | null;
}

const OktaSignInWidget = (_props: OktaInjectedProps): React.JSX.Element => {
  const oktaAuth = useOkta();
  const widgetRef = useRef<OktaSignIn | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;

    if (!el) return undefined;

    const oktaOptions = oktaAuth.options as typeof oktaAuth.options & { url?: string };

    widgetRef.current = new OktaSignIn({
      baseUrl: oktaOptions.url,
      clientId: oktaOptions.clientId,
      redirectUri: oktaOptions.redirectUri,
      useClassicEngine: true,
      authParams: {
        issuer: oktaOptions.issuer,
        responseType: ['code'],
        scopes: ['openid', 'profile', 'email', 'offline_access'],
      },
      features: {
        autoPush: true,
        rememberMe: true,
        registration: false,
        hideSignOutLinkInMFA: true,
      },
      brandName: 'MARKETview',
      logo: undefined,
      i18n: {
        en: {
          'primaryauth.title': 'Log In',
          'primaryauth.submit': 'Sign In',
          autoPush: 'Send push automatically next time',
          'enroll.choices.description.generic':
            'MARKETview requires multifactor authentication to add an additional layer of security when signing in to your account.',
          'enroll.totp.setupApp':
            'Launch the {0} application on your mobile device and select Add an account.',
        },
      },
    });

    widgetRef.current.showSignInAndRedirect({ el: el as unknown as string }).catch((err) => {
      console.error('SignIn Widget Error: ', err);
    });

    return () => {
      if (widgetRef.current) {
        widgetRef.current.remove();
      }
    };
  }, [oktaAuth]);

  return <div ref={containerRef} />;
};

export default withOktaAuth(OktaSignInWidget);
